import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Tabs from '@radix-ui/react-tabs';
import { MoreHorizontal } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Modal, Drawer, useToast, useConfirm, CountrySelect, ErrorState } from '@/components/ui';
import { accountsApi, Account } from '@/lib/accountsApi';
import { contactsApi } from '@/lib/contactsApi';
import { signalsApi, Signal } from '@/lib/signalsApi';
import { CommentThread } from '@/components/comments/CommentThread';
import { OrgChart } from '@/components/companies/OrgChart';
import { TechStackList } from '@/components/companies/TechStackList';
import { formatLocaleDate } from '@/lib/date';

const EMPLOYEE_BANDS: Array<{ value: string; labelKey: string }> = [
  { value: '1-10', labelKey: 'accounts.employees_1_10' },
  { value: '11-50', labelKey: 'accounts.employees_11_50' },
  { value: '51-200', labelKey: 'accounts.employees_51_200' },
  { value: '201-1000', labelKey: 'accounts.employees_201_1000' },
  { value: '1001+', labelKey: 'accounts.employees_1001_plus' },
];

interface AccountFormState {
  legalName: string;
  jpCorpNumber: string;
  domain: string;
  countryCode: string;
  industry: string;
  employeeBand: string;
  revenueBand: string;
}

const EMPTY_FORM: AccountFormState = {
  legalName: '',
  jpCorpNumber: '',
  domain: '',
  countryCode: '',
  industry: '',
  employeeBand: '',
  revenueBand: '',
};

function fromAccount(a: Account): AccountFormState {
  return {
    legalName: a.legalName ?? '',
    jpCorpNumber: a.jpCorpNumber ?? '',
    domain: a.domain ?? '',
    countryCode: a.countryCode ?? '',
    industry: a.industry ?? '',
    employeeBand: a.employeeBand ?? '',
    revenueBand: a.revenueBand ?? '',
  };
}

function AccountForm({
  initial,
  onCancel,
  onSubmit,
  submitting,
  submitLabel,
}: {
  initial: AccountFormState;
  onCancel: () => void;
  onSubmit: (values: AccountFormState) => void;
  submitting: boolean;
  submitLabel: string;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<AccountFormState>(initial);

  const set = <K extends keyof AccountFormState>(k: K, v: AccountFormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const corpNumberValid =
    form.jpCorpNumber === '' || /^[0-9]{13}$/.test(form.jpCorpNumber);
  const canSubmit =
    form.legalName.trim().length > 0 && corpNumberValid && !submitting;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) onSubmit(form);
      }}
      className="space-y-4"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block md:col-span-2">
          <span className="text-xs text-ink-600">
            {t('accounts.field_legal_name')}{' '}
            <span className="text-warn-600">*</span>
          </span>
          <input
            type="text"
            value={form.legalName}
            onChange={(e) => set('legalName', e.target.value)}
            required
            className="mt-1 w-full rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
          />
        </label>
        <label className="block">
          <span className="text-xs text-ink-600">
            {t('accounts.field_corp_number')}
          </span>
          <input
            type="text"
            value={form.jpCorpNumber}
            onChange={(e) =>
              set('jpCorpNumber', e.target.value.replace(/[^0-9]/g, ''))
            }
            inputMode="numeric"
            maxLength={13}
            placeholder="1234567890123"
            className="mt-1 w-full rounded-md border border-ink-300 px-3 py-1.5 text-sm num focus:outline-none focus:ring-2 focus:ring-accent-500/30"
          />
          <span className="text-[11px] text-ink-500">
            {t('accounts.field_corp_number_hint')}
          </span>
        </label>
        <label className="block">
          <span className="text-xs text-ink-600">{t('accounts.field_domain')}</span>
          <input
            type="text"
            value={form.domain}
            onChange={(e) => set('domain', e.target.value)}
            placeholder="example.co.jp"
            className="mt-1 w-full rounded-md border border-ink-300 px-3 py-1.5 text-sm num focus:outline-none focus:ring-2 focus:ring-accent-500/30"
          />
        </label>
        <label className="block">
          <span className="text-xs text-ink-600">{t('accounts.field_country')}</span>
          <div className="mt-1">
            <CountrySelect
              value={form.countryCode}
              onChange={(v) => set('countryCode', v)}
              allowEmpty={{ value: '', label: t('common.select_placeholder') }}
            />
          </div>
        </label>
        <label className="block">
          <span className="text-xs text-ink-600">{t('accounts.field_industry')}</span>
          <input
            type="text"
            value={form.industry}
            onChange={(e) => set('industry', e.target.value)}
            className="mt-1 w-full rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
          />
        </label>
        <label className="block">
          <span className="text-xs text-ink-600">{t('accounts.field_employees')}</span>
          <select
            value={form.employeeBand}
            onChange={(e) => set('employeeBand', e.target.value)}
            className="mt-1 w-full rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
          >
            <option value="">{t('common.select_placeholder')}</option>
            {EMPLOYEE_BANDS.map((b) => (
              <option key={b.value} value={b.value}>
                {t(b.labelKey)}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs text-ink-600">{t('accounts.field_revenue')}</span>
          <input
            type="text"
            value={form.revenueBand}
            onChange={(e) => set('revenueBand', e.target.value)}
            className="mt-1 w-full rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
          />
        </label>
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="text-sm px-3 py-1.5 rounded-md text-ink-700 hover:bg-ink-100"
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className="text-sm px-3 py-1.5 rounded-md bg-accent-500 text-white font-medium hover:bg-accent-600 disabled:opacity-60"
        >
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

function SignalList({ items }: { items: Signal[] }) {
  const { t, i18n } = useTranslation();
  if (items.length === 0) {
    return (
      <p className="text-xs text-ink-500">{t('accounts.drawer_no_signals')}</p>
    );
  }
  return (
    <ul className="space-y-2">
      {items.map((s) => (
        <li key={s.id} className="rounded-md border border-ink-200 p-3 text-xs">
          <div className="font-medium text-ink-900">
            {String(s.payload?.title ?? s.kind)}
          </div>
          <div className="text-ink-500 num mt-0.5">
            {formatLocaleDate(s.occurredAt, i18n.language)} · {s.source}
          </div>
        </li>
      ))}
    </ul>
  );
}

const TAB_TRIGGER =
  'px-3 py-2 text-xs uppercase tracking-[0.12em] font-semibold text-ink-500 hover:text-ink-900 border-b-2 border-transparent data-[state=active]:border-accent-500 data-[state=active]:text-ink-900 transition-colors';

function AccountDrawerBody({
  wsId,
  account,
}: {
  wsId: string;
  account: Account;
}) {
  const { t } = useTranslation();
  const contacts = useQuery({
    queryKey: ['contacts', wsId, 'by-account', account.id],
    queryFn: () =>
      contactsApi.list({ workspaceId: wsId, accountId: account.id, limit: 50 }),
    enabled: !!wsId,
  });
  const allSignals = useQuery({
    queryKey: ['signals', 'account', account.id, 'all'],
    queryFn: () =>
      signalsApi.list({ workspaceId: wsId, accountId: account.id, limit: 50 }),
    enabled: !!wsId,
  });

  const signals = allSignals.data?.items ?? [];
  const pressSignals = signals.filter((s) => s.kind === 'press' || s.kind === 'tdnet');
  const hiringSignals = signals.filter((s) => s.kind === 'hiring_spike');
  const contactList = contacts.data?.items ?? [];

  return (
    <Tabs.Root defaultValue="profile" className="text-sm">
      <Tabs.List className="flex gap-1 border-b border-ink-200 mb-4 overflow-x-auto">
        <Tabs.Trigger value="profile" className={TAB_TRIGGER}>
          {t('accounts.tab_profile')}
        </Tabs.Trigger>
        <Tabs.Trigger value="signals" className={TAB_TRIGGER}>
          {t('accounts.tab_signals')}
          {signals.length > 0 && (
            <span className="ml-1.5 num text-[10px]">({signals.length})</span>
          )}
        </Tabs.Trigger>
        <Tabs.Trigger value="org" className={TAB_TRIGGER}>
          {t('accounts.tab_org')}
        </Tabs.Trigger>
        <Tabs.Trigger value="tech" className={TAB_TRIGGER}>
          {t('accounts.tab_tech')}
        </Tabs.Trigger>
        <Tabs.Trigger value="press" className={TAB_TRIGGER}>
          {t('accounts.tab_press')}
          {pressSignals.length > 0 && (
            <span className="ml-1.5 num text-[10px]">({pressSignals.length})</span>
          )}
        </Tabs.Trigger>
        <Tabs.Trigger value="hiring" className={TAB_TRIGGER}>
          {t('accounts.tab_hiring')}
          {hiringSignals.length > 0 && (
            <span className="ml-1.5 num text-[10px]">({hiringSignals.length})</span>
          )}
        </Tabs.Trigger>
        <Tabs.Trigger value="comments" className={TAB_TRIGGER}>
          {t('accounts.tab_comments')}
        </Tabs.Trigger>
      </Tabs.List>

      <Tabs.Content value="profile" className="space-y-6">
        <section>
          <h3 className="text-xs uppercase tracking-wide text-ink-500 mb-2">
            {t('accounts.drawer_overview')}
          </h3>
          <dl className="space-y-1.5">
            <div className="flex justify-between gap-3">
              <dt className="text-ink-500">{t('accounts.field_corp_number')}</dt>
              <dd className="text-ink-900 num">{account.jpCorpNumber || '—'}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-ink-500">{t('accounts.field_domain')}</dt>
              <dd className="text-ink-900 num">{account.domain || '—'}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-ink-500">{t('accounts.field_country')}</dt>
              <dd className="text-ink-900">{account.countryCode || '—'}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-ink-500">{t('accounts.field_industry')}</dt>
              <dd className="text-ink-900">{account.industry || '—'}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-ink-500">{t('accounts.field_employees')}</dt>
              <dd className="text-ink-900">{account.employeeBand || '—'}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-ink-500">{t('accounts.field_revenue')}</dt>
              <dd className="text-ink-900">{account.revenueBand || '—'}</dd>
            </div>
          </dl>
        </section>

        <section>
          <h3 className="text-xs uppercase tracking-wide text-ink-500 mb-2">
            {t('accounts.drawer_contacts')}
          </h3>
          {contacts.isLoading && (
            <p className="text-xs text-ink-500">{t('common.loading')}</p>
          )}
          {!contacts.isLoading && contactList.length === 0 && (
            <p className="text-xs text-ink-500">
              {t('accounts.drawer_no_contacts')}
            </p>
          )}
          <ul className="space-y-2">
            {contactList.slice(0, 10).map((c) => (
              <li
                key={c.id}
                className="rounded-md border border-ink-200 p-3 text-xs"
              >
                <div className="font-medium text-ink-900">{c.displayName}</div>
                <div className="text-ink-500 mt-0.5">
                  {c.title || '—'}
                  {c.email && <span className="num"> · {c.email}</span>}
                </div>
              </li>
            ))}
          </ul>
        </section>
      </Tabs.Content>

      <Tabs.Content value="signals">
        {allSignals.isLoading ? (
          <p className="text-xs text-ink-500">{t('common.loading')}</p>
        ) : (
          <SignalList items={signals} />
        )}
      </Tabs.Content>

      <Tabs.Content value="org">
        {contacts.isLoading ? (
          <p className="text-xs text-ink-500">{t('common.loading')}</p>
        ) : (
          <OrgChart contacts={contactList} />
        )}
      </Tabs.Content>

      <Tabs.Content value="tech">
        <TechStackList items={account.techStack || []} />
      </Tabs.Content>

      <Tabs.Content value="press">
        {allSignals.isLoading ? (
          <p className="text-xs text-ink-500">{t('common.loading')}</p>
        ) : (
          <SignalList items={pressSignals} />
        )}
      </Tabs.Content>

      <Tabs.Content value="hiring">
        {allSignals.isLoading ? (
          <p className="text-xs text-ink-500">{t('common.loading')}</p>
        ) : (
          <SignalList items={hiringSignals} />
        )}
      </Tabs.Content>

      <Tabs.Content value="comments">
        <CommentThread
          workspaceId={wsId}
          targetKind="account"
          targetId={account.id}
        />
      </Tabs.Content>
    </Tabs.Root>
  );
}

export function CompaniesPage() {
  const { wsId = '' } = useParams<{ wsId: string }>();
  const [search, setSearch] = useState('');
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Account | null>(null);
  const [drawerAccount, setDrawerAccount] = useState<Account | null>(null);
  const [fuzzy, setFuzzy] = useState(false);

  const fuzzyActive = fuzzy && search.trim().length > 0;

  const list = useQuery({
    queryKey: ['accounts', wsId, search, fuzzyActive],
    queryFn: async (): Promise<{ items: Account[] }> => {
      if (fuzzyActive) {
        const r = await accountsApi.searchSemantic({ workspaceId: wsId, q: search.trim(), limit: 50 });
        return { items: r.items };
      }
      const r = await accountsApi.list({ workspaceId: wsId, search: search || undefined, limit: 100 });
      return { items: r.items };
    },
    enabled: !!wsId,
  });

  const reindex = useMutation({
    mutationFn: () => accountsApi.reindex(wsId),
    onSuccess: (r) => {
      if (r.enabled) toast.success(t('common.reindexed', { count: r.indexed }));
      else toast.error(t('common.semantic_unavailable'));
    },
    onError: () => toast.error(t('common.semantic_unavailable')),
  });

  const items: Account[] = list.data?.items ?? [];
  const total = items.length;

  const toApi = (v: AccountFormState): Partial<Account> => ({
    legalName: v.legalName,
    jpCorpNumber: v.jpCorpNumber || null,
    domain: v.domain || null,
    countryCode: v.countryCode || null,
    industry: v.industry || null,
    employeeBand: v.employeeBand || null,
    revenueBand: v.revenueBand || null,
  });

  const create = useMutation({
    mutationFn: (v: AccountFormState) =>
      accountsApi.create({
        workspaceId: wsId,
        legalName: v.legalName,
        ...toApi(v),
      } as Partial<Account> & { workspaceId: string; legalName: string }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounts', wsId] });
      setCreateOpen(false);
      toast.success(t('accounts.created'));
    },
    onError: () => toast.error(t('accounts.create_failed')),
  });

  const update = useMutation({
    mutationFn: ({ id, values }: { id: string; values: AccountFormState }) =>
      accountsApi.update(id, toApi(values)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounts', wsId] });
      setEditing(null);
      toast.success(t('accounts.updated'));
    },
    onError: () => toast.error(t('common.save_failed')),
  });

  const remove = useMutation({
    mutationFn: (id: string) => accountsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounts', wsId] });
      toast.success(t('accounts.deleted'));
    },
    onError: () => toast.error(t('common.delete_failed')),
  });

  const askDelete = async (a: Account) => {
    const ok = await confirm({
      title: t('accounts.confirm_delete_title'),
      description: t('accounts.confirm_delete_body', { name: a.legalName }),
      confirmLabel: t('common.delete'),
      variant: 'danger',
    });
    if (ok) remove.mutate(a.id);
  };

  return (
    <AppShell titleKey="accounts.title">
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <input
          type="search"
          placeholder={t('common.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-64 rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
        />
        <label
          className="flex items-center gap-1.5 text-xs text-ink-600 cursor-pointer"
          title={t('common.fuzzy_hint')}
        >
          <input type="checkbox" checked={fuzzy} onChange={(e) => setFuzzy(e.target.checked)} />
          {t('common.fuzzy')}
        </label>
        <span className="text-xs text-ink-500">
          {t('accounts.count_label', { count: total })}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => reindex.mutate()}
            disabled={reindex.isPending}
            className="text-xs px-3 py-1.5 rounded-md border border-ink-300 text-ink-700 hover:bg-ink-50 disabled:opacity-50"
          >
            {t('common.reindex')}
          </button>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="text-xs px-3 py-1.5 rounded-md bg-accent-500 text-white hover:bg-accent-600"
          >
            {t('accounts.new_button')}
          </button>
        </div>
      </div>

      {list.isLoading && <p className="text-sm text-ink-500">{t('common.loading')}</p>}
      {list.isError && <ErrorState onRetry={() => list.refetch()} />}
      {!list.isLoading && !list.isError && items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-ink-300 p-10 text-center text-ink-500">
          <p className="text-sm">{t('accounts.empty_title')}</p>
          <p className="text-xs mt-2">{t('accounts.empty_sub')}</p>
        </div>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm bg-white rounded-2xl border border-ink-200">
            <thead className="text-left text-ink-500 border-b border-ink-200">
              <tr>
                <th className="py-2.5 px-3 font-medium">{t('accounts.col_legal_name')}</th>
                <th className="py-2.5 px-3 font-medium">{t('accounts.col_corp_number')}</th>
                <th className="py-2.5 px-3 font-medium">{t('accounts.col_domain')}</th>
                <th className="py-2.5 px-3 font-medium">{t('accounts.col_country')}</th>
                <th className="py-2.5 px-3 font-medium">{t('accounts.col_industry')}</th>
                <th className="py-2.5 px-3 font-medium">{t('accounts.col_employees')}</th>
                <th className="py-2.5 px-3 font-medium">{t('accounts.col_health')}</th>
                <th className="py-2.5 px-3 font-medium w-10" aria-label={t('common.actions')} />
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr
                  key={a.id}
                  className="border-b border-ink-100 hover:bg-ink-50 cursor-pointer"
                  onClick={() => setDrawerAccount(a)}
                >
                  <td className="py-2.5 px-3 font-medium text-ink-900">{a.legalName}</td>
                  <td className="py-2.5 px-3 num text-ink-700">{a.jpCorpNumber || '—'}</td>
                  <td className="py-2.5 px-3 num text-ink-700">{a.domain || '—'}</td>
                  <td className="py-2.5 px-3 text-ink-700">{a.countryCode || '—'}</td>
                  <td className="py-2.5 px-3 text-ink-700">{a.industry || '—'}</td>
                  <td className="py-2.5 px-3 text-ink-700">{a.employeeBand || '—'}</td>
                  <td className="py-2.5 px-3 num text-ink-700">{a.healthScore ?? '—'}</td>
                  <td
                    className="py-2.5 px-3 text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DropdownMenu.Root>
                      <DropdownMenu.Trigger asChild>
                        <button
                          type="button"
                          aria-label={t('accounts.row_actions')}
                          className="rounded-md p-1 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Portal>
                        <DropdownMenu.Content
                          align="end"
                          sideOffset={4}
                          className="min-w-[140px] rounded-md border border-ink-200 bg-white p-1 text-sm"
                        >
                          <DropdownMenu.Item
                            onSelect={() => setEditing(a)}
                            className="cursor-pointer rounded-sm px-2 py-1 text-ink-700 outline-none data-[highlighted]:bg-ink-100"
                          >
                            {t('common.edit')}
                          </DropdownMenu.Item>
                          <DropdownMenu.Item
                            onSelect={() => askDelete(a)}
                            className="cursor-pointer rounded-sm px-2 py-1 text-warn-600 outline-none data-[highlighted]:bg-warn-50"
                          >
                            {t('common.delete')}
                          </DropdownMenu.Item>
                        </DropdownMenu.Content>
                      </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={createOpen}
        onOpenChange={setCreateOpen}
        title={t('accounts.new_title')}
        size="lg"
      >
        <AccountForm
          initial={EMPTY_FORM}
          onCancel={() => setCreateOpen(false)}
          onSubmit={(v) => create.mutate(v)}
          submitting={create.isPending}
          submitLabel={create.isPending ? t('common.creating') : t('common.create')}
        />
      </Modal>

      <Modal
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        title={t('accounts.edit_title')}
        size="lg"
      >
        {editing && (
          <AccountForm
            initial={fromAccount(editing)}
            onCancel={() => setEditing(null)}
            onSubmit={(values) => update.mutate({ id: editing.id, values })}
            submitting={update.isPending}
            submitLabel={
              update.isPending ? t('common.updating') : t('common.save')
            }
          />
        )}
      </Modal>

      <Drawer
        open={!!drawerAccount}
        onOpenChange={(o) => !o && setDrawerAccount(null)}
        title={drawerAccount?.legalName ?? ''}
        description={drawerAccount?.domain ?? undefined}
      >
        {drawerAccount && (
          <AccountDrawerBody wsId={wsId} account={drawerAccount} />
        )}
      </Drawer>
    </AppShell>
  );
}
